import {
    AcademicCapIcon,
    ArrowDownTrayIcon,
    ArrowLeftIcon,
    ArrowRightIcon,
    ArrowPathIcon,
    CalendarIcon,
    CheckCircleIcon,
    ClockIcon,
    CurrencyRupeeIcon,
    DocumentTextIcon,
    DocumentIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    IdentificationIcon,
    MapPinIcon,
    PencilIcon,
    PhoneIcon,
    PlusIcon,
    TrashIcon,
    UserIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiError';
import { validatePhone, validateEmail } from '../lib/phoneEmailValidation';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getToken = () => localStorage.getItem('admin_token');

// ── Schedule helpers ──────────────────────────────────────────────────────────
const _fmt12h = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const _DAY_SHORT  = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat', Sunday:'Sun' };
const _DAYS_ORDER = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

const formatScheduleCompact = (schedule) => {
  if (!schedule?.length) return null;
  const groups = [];
  schedule.forEach((s) => {
    const last = groups[groups.length - 1];
    const sameTime    = last && last.startTime === s.startTime && last.endTime === s.endTime;
    const consecutive = last && _DAYS_ORDER.indexOf(s.day) === _DAYS_ORDER.indexOf(last.lastDay) + 1;
    if (sameTime && consecutive) {
      last.lastDay = s.day;
    } else {
      groups.push({ firstDay: s.day, lastDay: s.day, startTime: s.startTime, endTime: s.endTime });
    }
  });
  return groups.map(({ firstDay, lastDay, startTime, endTime }) => {
    const dayPart  = firstDay === lastDay ? (_DAY_SHORT[firstDay] || firstDay) : `${_DAY_SHORT[firstDay] || firstDay} – ${_DAY_SHORT[lastDay] || lastDay}`;
    const timePart = startTime && endTime ? ` (${_fmt12h(startTime)} – ${_fmt12h(endTime)})` : '';
    return `${dayPart}${timePart}`;
  }).join(', ');
};

// =============== SKELETON LOADER COMPONENTS ===============

const SkeletonPulse = ({ className = "", delay = 0 }) => (
  <div 
    className={`animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded ${className}`}
    style={{ 
      animationDelay: `${delay}ms`,
      animationDuration: '1.5s'
    }}
  />
);

const BubbleLoader = ({ className = "", color = "blue" }) => {
  const colorClasses = {
    blue: 'from-blue-400 to-blue-600',
    violet: 'from-violet-400 to-violet-600',
    green: 'from-green-400 to-green-600',
    orange: 'from-orange-400 to-orange-600',
  };

  return (
    <div className={`relative ${className}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${colorClasses[color]} opacity-20 rounded-full animate-[ping_2s_ease-in-out_infinite]`} />
      <div className={`absolute inset-0 bg-gradient-to-br ${colorClasses[color]} opacity-30 rounded-full animate-[pulse_1.5s_ease-in-out_infinite]`} />
    </div>
  );
};

const HeaderSkeleton = () => (
  <div className="flex items-center justify-between animate-fadeIn">
    <div className="flex items-center gap-4">
      <SkeletonPulse className="h-10 w-32" delay={0} />
      <div>
        <SkeletonPulse className="h-8 w-48 mb-2" delay={100} />
        <SkeletonPulse className="h-4 w-32" delay={200} />
      </div>
    </div>
    <div className="flex items-center gap-2">
      <SkeletonPulse className="h-8 w-20 rounded-full" delay={300} />
      <SkeletonPulse className="h-10 w-32" delay={400} />
      <SkeletonPulse className="h-10 w-40" delay={500} />
    </div>
  </div>
);

const StatCardSkeleton = ({ color = "blue", delay = 0 }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    violet: 'bg-violet-50 border-violet-200',
    green: 'bg-green-50 border-green-200',
    orange: 'bg-orange-50 border-orange-200',
  };

  return (
    <Card className={`${colorClasses[color]} border-2 overflow-hidden relative animate-scaleIn`} style={{ animationDelay: `${delay}ms` }}>
      <CardContent className="p-4 text-center">
        <div className="flex items-center justify-center mb-3 relative">
          <BubbleLoader className="h-12 w-12" color={color} />
        </div>
        <SkeletonPulse className="h-8 w-20 mx-auto mb-2" delay={delay + 100} />
        <SkeletonPulse className="h-4 w-24 mx-auto" delay={delay + 200} />
      </CardContent>
    </Card>
  );
};

const InfoCardSkeleton = ({ title, icon: Icon, iconColor, rows = 4, delay = 0 }) => (
  <Card className="overflow-hidden animate-slideInLeft" style={{ animationDelay: `${delay}ms` }}>
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2">
        <div className="relative">
          <Icon className={`h-5 w-5 ${iconColor} animate-bounce`} style={{ animationDelay: `${delay}ms` }} />
          <div className={`absolute inset-0 ${iconColor.replace('text', 'bg')} opacity-20 rounded-full animate-ping`} style={{ animationDelay: `${delay}ms` }} />
        </div>
        <span>{title}</span>
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      {Array.from({ length: rows }).map((_, idx) => (
        <div key={idx} className="flex items-start gap-3">
          <SkeletonPulse className="h-5 w-5 rounded flex-shrink-0" delay={delay + (idx * 100)} />
          <div className="flex-1 space-y-2">
            <SkeletonPulse className="h-3 w-20" delay={delay + (idx * 100) + 50} />
            <SkeletonPulse className="h-4 w-full" delay={delay + (idx * 100) + 100} />
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const CourseCardSkeleton = ({ delay = 0 }) => (
  <Card className="border-2 animate-slideInRight overflow-hidden" style={{ animationDelay: `${delay}ms` }}>
    <CardContent className="p-4">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <SkeletonPulse className="h-6 w-3/4 mb-2" delay={delay} />
          <SkeletonPulse className="h-4 w-32" delay={delay + 100} />
        </div>
        <SkeletonPulse className="h-6 w-20 rounded-full" delay={delay + 200} />
      </div>

      {/* Batch Section */}
      <div className="mb-4 p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full opacity-20 -mr-16 -mt-16 animate-pulse" />
        <SkeletonPulse className="h-4 w-24 mb-3" delay={delay + 300} />
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx}>
              <SkeletonPulse className="h-3 w-16 mb-1" delay={delay + 400 + (idx * 50)} />
              <SkeletonPulse className="h-4 w-full" delay={delay + 450 + (idx * 50)} />
            </div>
          ))}
        </div>
      </div>

      {/* Trainer Section */}
      <div className="mb-4 p-4 bg-gradient-to-br from-violet-50 to-violet-100 rounded-lg relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-violet-200 rounded-full opacity-20 -ml-16 -mb-16 animate-pulse" style={{ animationDelay: `${delay + 100}ms` }} />
        <SkeletonPulse className="h-4 w-28 mb-3" delay={delay + 700} />
        <div className="flex items-center gap-3">
          <BubbleLoader className="h-12 w-12 rounded-full" color="violet" />
          <div className="flex-1 space-y-2">
            <SkeletonPulse className="h-4 w-32" delay={delay + 800} />
            <SkeletonPulse className="h-3 w-48" delay={delay + 900} />
          </div>
        </div>
      </div>

      {/* Payment Section */}
      <div className="grid grid-cols-3 gap-4 p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg">
        {['green', 'blue', 'orange'].map((color, idx) => (
          <div key={idx}>
            <SkeletonPulse className="h-3 w-full mb-2" delay={delay + 1000 + (idx * 100)} />
            <SkeletonPulse className="h-5 w-16" delay={delay + 1050 + (idx * 100)} />
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const LoadingState = () => (
  <div className="space-y-6 text-sm font-sans leading-relaxed p-6">
    <style>{`
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideInLeft {
        from { opacity: 0; transform: translateX(-20px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideInRight {
        from { opacity: 0; transform: translateX(20px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes scaleIn {
        from { opacity: 0; transform: scale(0.9); }
        to { opacity: 1; transform: scale(1); }
      }
      .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
      .animate-slideInLeft { animation: slideInLeft 0.5s ease-out forwards; }
      .animate-slideInRight { animation: slideInRight 0.5s ease-out forwards; }
      .animate-scaleIn { animation: scaleIn 0.4s ease-out forwards; }
    `}</style>

    {/* Header Skeleton */}
    <HeaderSkeleton />

    {/* Stats Cards Skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <StatCardSkeleton color="blue" delay={0} />
      <StatCardSkeleton color="green" delay={100} />
      <StatCardSkeleton color="orange" delay={200} />
      <StatCardSkeleton color="violet" delay={300} />
    </div>

    {/* Main Content Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column */}
      <div className="lg:col-span-1 space-y-6">
        <InfoCardSkeleton
          title="Basic Information"
          icon={UserIcon}
          iconColor="text-blue-600"
          rows={4}
          delay={0}
        />
        {/* <InfoCardSkeleton
          title="Guardian Information"
          icon={UsersIcon}
          iconColor="text-violet-600"
          rows={4}
          delay={100}
        /> */}
        <InfoCardSkeleton
          title="Documents"
          icon={DocumentIcon}
          iconColor="text-green-600"
          rows={3}
          delay={200}
        />
      </div>

      {/* Right Column */}
      <div className="lg:col-span-2 space-y-6">
        <Card className="animate-fadeIn" style={{ animationDelay: '300ms' }}>
          <CardHeader>
            <div className="flex items-center gap-2">
              <SkeletonPulse className="h-6 w-48" />
            </div>
            <SkeletonPulse className="h-4 w-64 mt-1" />
          </CardHeader>
          <CardContent className="space-y-4">
            <CourseCardSkeleton delay={0} />
            <CourseCardSkeleton delay={200} />
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
);

// =============== MAIN COMPONENT ===============

export default function StudentProfile() {
  usePageMeta(PAGE_META.studentProfile);
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Edit student dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editedStudent, setEditedStudent] = useState(null);

  // Enroll in new course dialog
  const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [enrollmentData, setEnrollmentData] = useState({
    courseId: '',
    batchId: '',
    paymentPlan: 'full',
    numInstallments: 2,
    installmentAmounts: [],   // per-installment custom amounts for EMI
    customTotalAmount: '',
    discountReason: ''
  });

  const blankEnrollment = {
    courseId: '',
    batchId: '',
    paymentPlan: 'full',
    numInstallments: 2,
    installmentAmounts: [],
    customTotalAmount: '',
    discountReason: ''
  };

  // File upload states
  const [uploadingFile, setUploadingFile] = useState(false);
  const [currentUploadType, setCurrentUploadType] = useState('');
  const [removingDocType, setRemovingDocType] = useState(null);

  // Complete history modal
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyData, setHistoryData] = useState({ attendances: [], feeData: null });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exportingHistory, setExportingHistory] = useState(false);

  const fetchCompleteHistory = async () => {
    if (!studentId) return;
    setHistoryLoading(true);
    setHistoryData({ attendances: [], feeData: null, refunds: [] });
    try {
      const token = getToken();
      const [attendanceRes, feeRes, refundsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/attendance/student/${studentId}`, {
          params: { limit: 5000 },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/fees/student/${studentId}/export`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API_BASE_URL}/student-discontinue/refunds/${studentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { data: [] } }))
      ]);
      setHistoryData({
        attendances: attendanceRes.data?.attendances || [],
        feeData: feeRes.data,
        refunds: refundsRes.data?.data || []
      });
    } catch (err) {
      console.error(err);
      toast.error(getApiErrorMessage(err, 'Failed to load history'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleViewCompleteHistory = () => {
    setHistoryModalOpen(true);
    fetchCompleteHistory();
  };

  const handleExportCompleteHistory = async () => {
    if (!studentId) return;
    setExportingHistory(true);
    try {
      const token = getToken();
      const [attendanceRes, feeRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/attendance/student/${studentId}`, {
          params: { limit: 5000 },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/fees/student/${studentId}/export`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      const attendances = attendanceRes.data?.attendances || [];
      const feeData = feeRes.data || {};

      const escapeCsv = (v) => {
        const s = String(v ?? '');
        if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '';
      const sections = [];
      sections.push(['STUDENT INFORMATION']);
      sections.push(['Name', feeData?.student?.name || student?.name]);
      sections.push(['Email', feeData?.student?.email || student?.email]);
      sections.push(['Phone', feeData?.student?.phone || student?.phone]);
      sections.push([]);
      sections.push(['ENROLLMENTS']);
      sections.push(['Course', 'Batch', 'Total Fee', 'Status']);
      (feeData?.courses || []).forEach((c) => sections.push([c.course, c.batch, c.totalFee, c.status]));
      sections.push([]);
      sections.push(['PAYMENT HISTORY']);
      sections.push(['Date', 'Receipt No', 'Amount', 'Mode', 'Installment', 'Recorded By']);
      (feeData?.payments || []).forEach((p) => sections.push([fmtDate(p.date), p.receiptNo, p.amount, p.mode, p.installmentNo, p.recordedBy]));
      sections.push([]);
      sections.push(['ATTENDANCE']);
      sections.push(['Date', 'Batch', 'Status', 'Reason']);
      attendances.forEach((a) => sections.push([fmtDate(a.date), a.batchId?.name || '-', a.status, a.reason || a.notes || '-']));

      const csv = sections.map((row) => (Array.isArray(row) ? row : [row]).map(escapeCsv).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `complete-history-${(student?.name || studentId).replace(/\s+/g, '-')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Complete history exported');
    } catch (err) {
      console.error(err);
      toast.error(getApiErrorMessage(err, 'Failed to export'));
    } finally {
      setExportingHistory(false);
    }
  };

  useEffect(() => {
    fetchStudentProfile();
    fetchCourses();
  }, [studentId]);

  const fetchStudentProfile = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_BASE_URL}/student-dashboard/${studentId}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.data?.success && response.data?.data) {
        setStudent(response.data.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(getApiErrorMessage(error, 'Student not found'));
        navigate('/students');
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to load student details'));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_BASE_URL}/student-dashboard/courses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.data?.success && Array.isArray(response.data?.data)) {
        // console.log(response.data.data);
        setCourses(response.data.data);
      }
    } catch (error) {
      if (error.response?.status >= 400) {
        setCourses([]);
      }
    }
  };

  const fetchBatchesForCourse = async (courseId) => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_BASE_URL}/student-dashboard/courses/${courseId}/batches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.data?.success && Array.isArray(response.data?.data)) {
        setAvailableBatches(response.data.data);
      } else {
        setAvailableBatches([]);
      }
    } catch (_error) {
      setAvailableBatches([]);
    }
  };

  // File upload handler
  const handleFileUpload = async (file, documentType) => {
    try {
      setUploadingFile(true);
      setCurrentUploadType(documentType);
      
      const formData = new FormData();
      formData.append('file', file);

      const token = getToken();
      const response = await axios.post(`${API_BASE_URL}/storage/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        const newDocument = {
          type: documentType,
          fileName: response.data.originalName,
          fileUrl: response.data.url
        };

        // Update the edited student state with new document
        const updatedDocuments = [...(editedStudent.documents || [])];
        const existingDocIndex = updatedDocuments.findIndex(doc => doc.type === documentType);
        
        if (existingDocIndex >= 0) {
          // If document exists, delete old file first
          await deleteFile(updatedDocuments[existingDocIndex].fileUrl);
          updatedDocuments[existingDocIndex] = newDocument;
        } else {
          updatedDocuments.push(newDocument);
        }

        setEditedStudent({
          ...editedStudent,
          documents: updatedDocuments
        });

        toast.success(`${documentType.replace('-', ' ')} uploaded successfully!`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(`Failed to upload ${documentType.replace('-', ' ')}`);
    } finally {
      setUploadingFile(false);
      setCurrentUploadType('');
    }
  };

  // File delete handler
  const deleteFile = async (fileUrl) => {
    try {
      const token = getToken();
      await axios.delete(`${API_BASE_URL}/storage/delete`, {
        data: { uri: fileUrl },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  // Remove document handler
  const handleRemoveDocument = async (documentType, fileUrl) => {
    setRemovingDocType(documentType);
    try {
      await deleteFile(fileUrl);
      const updatedDocuments = editedStudent.documents.filter(doc => doc.type !== documentType);
      setEditedStudent({
        ...editedStudent,
        documents: updatedDocuments
      });
      toast.success(`${documentType.replace('-', ' ')} removed successfully!`);
    } catch (error) {
      console.error('Error removing document:', error);
      toast.error(getApiErrorMessage(error, 'Failed to remove document'));
    } finally {
      setRemovingDocType(null);
    }
  };

  // Open document in new tab
  const handleOpenDocument = (fileUrl) => {
    window.open(fileUrl, '_blank');
  };

  const handleEditStudent = () => {
    if (student) {
      setEditedStudent({
        name: student.name,
        email: student.email,
        phone: student.phone,
        // guardian: { ...student.guardian },
        address: { ...student.address },
        admissionSource: student.admissionSource,
        documents: student.documents || [],
        status: student.status
      });
      setIsEditDialogOpen(true);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();

    const phoneResult = validatePhone(editedStudent.phone);
    if (!phoneResult.valid) { toast.error(phoneResult.message); return; }

    const emailResult = validateEmail(editedStudent.email, { required: true });
    if (!emailResult.valid) { toast.error(emailResult.message); return; }

    try {
      const token = getToken();
      setLoading(true);
      
      const response = await axios.put(
        `${API_BASE_URL}/students/${studentId}`,
        { ...editedStudent, phone: phoneResult.cleaned, email: emailResult.cleaned },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data) {
        setStudent({ ...student, ...response.data });
        setIsEditDialogOpen(false);
        toast.success('Student details updated successfully!');
        fetchStudentProfile();
      }
    } catch (error) {
      console.error('Error updating student:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update student details'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollInCourse = () => {
    setIsEnrollDialogOpen(true);
  };

  const handleCourseChange = async (courseId) => {
    setEnrollmentData({
      ...enrollmentData,
      courseId: courseId,
      batchId: '',
      installmentAmounts: [],
      customTotalAmount: '',
      discountReason: ''
    });

    if (courseId) {
      await fetchBatchesForCourse(courseId);
    } else {
      setAvailableBatches([]);
    }
  };

  const isBatchFull = (batch) => {
    return batch.assignedStudents >= batch.capacity;
  };

  const handleEnrollmentSubmit = async (e) => {
    e.preventDefault();

    if (!enrollmentData.courseId || !enrollmentData.batchId) {
      toast.error('Please select course and batch');
      return;
    }

    try {
      const token = getToken();
      setLoading(true);

      const finalFee = enrollmentData.customTotalAmount
        ? Number(enrollmentData.customTotalAmount)
        : applicableFee;

      // Validate custom installment amounts when set
      if (enrollmentData.paymentPlan === 'emi' && enrollmentData.installmentAmounts.length > 0) {
        const amtSum = enrollmentData.installmentAmounts.reduce((s, a) => s + (Number(a) || 0), 0);
        if (Math.abs(amtSum - finalFee) > 1) {
          toast.error(`Installment amounts must total ₹${finalFee.toLocaleString()} (current total: ₹${amtSum.toLocaleString()})`);
          setLoading(false);
          return;
        }
      }

      const enrollmentPayload = {
        studentId: studentId,
        batchId: enrollmentData.batchId,
        paymentPlan: enrollmentData.paymentPlan
      };

      if (enrollmentData.paymentPlan === 'emi') {
        enrollmentPayload.numInstallments = Number(enrollmentData.numInstallments);
        if (enrollmentData.installmentAmounts.length === enrollmentData.numInstallments) {
          enrollmentPayload.installmentAmounts = enrollmentData.installmentAmounts.map(Number);
        }
      }
      if (enrollmentData.customTotalAmount) {
        enrollmentPayload.customTotalAmount = Number(enrollmentData.customTotalAmount);
      }
      if (enrollmentData.discountReason) {
        enrollmentPayload.discountReason = enrollmentData.discountReason.trim();
      }

      const response = await axios.post(
        `${API_BASE_URL}/students/enroll`,
        enrollmentPayload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        // Close first, then show success — modal gone before toast appears
        setIsEnrollDialogOpen(false);
        setEnrollmentData(blankEnrollment);
        setAvailableBatches([]);
        setTimeout(() => toast.success('Student enrolled successfully!'), 50);
        await fetchStudentProfile();
      } else {
        // Keep dialog open on failure
        toast.error(response.data.message || 'Enrollment failed. Please try again.');
      }
    } catch (error) {
      console.error('Error enrolling student:', error);
      toast.error(getApiErrorMessage(error, 'Failed to enroll student'));
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'emi': return 'bg-orange-100 text-orange-800';
      case 'pending': return 'bg-red-100 text-red-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDocumentDisplayName = (type) => {
    const names = {
      'id': 'ID Proof',
      // '10th-certificate': '10th Certificate',
      // '12th-certificate': '12th Certificate',
      // 'degree-certificate': 'Degree Certificate',
      'photo': 'Photo',
      'adhaar': 'Aadhaar Card',
      'pancard': 'PAN Card'
    };
    return names[type] || type;
  };

  const selectedCourse = courses.find(c => c._id === enrollmentData.courseId);
  const selectedBatch = availableBatches.find(b => b._id === enrollmentData.batchId);

  // Fee applicable to the selected batch mode; falls back to onlineFee when no batch chosen yet
  const applicableFee = selectedCourse
    ? (selectedBatch?.mode === 'offline'
        ? (selectedCourse.offlineFee ?? selectedCourse.onlineFee ?? 0)
        : (selectedCourse.onlineFee ?? selectedCourse.offlineFee ?? 0))
    : 0;

  // Show loading skeleton
  if (loading && !student) {
    return <LoadingState />;
  }

  if (!student) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-lg text-gray-600">Student not found</p>
          <Button onClick={() => navigate('/students')} className="mt-4">
            Back to Students
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-sm font-sans leading-relaxed p-6 bg-gradient-to-br from-gray-50/80 to-violet-50/30 min-h-screen">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="glass"
            onClick={() => navigate('/students')}
            className="flex items-center gap-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Students
          </Button>
          <div>
            <p className="text-gray-600 font-data text-sm">Student Profile</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={getPaymentStatusColor(student.status) + " text-xs px-3 py-1.5"}>
            {student.status ? student.status.charAt(0).toUpperCase() + student.status.slice(1) : 'Active'}
          </Badge>
          <Button
            variant="glass"
            size="sm"
            onClick={handleViewCompleteHistory}
            disabled={loading}
            className="flex items-center gap-2"
            title="View and export complete student history"
          >
            <DocumentTextIcon className="h-4 w-4" />
            Complete History
          </Button>
          <Button
            variant="glass"
            onClick={handleEditStudent}
            className="flex items-center gap-2"
            disabled={loading}
          >
            <PencilIcon className="h-4 w-4" />
            Edit Details
          </Button>
          <Button
            onClick={handleEnrollInCourse}
            className="bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white flex items-center gap-2"
            disabled={loading}
          >
            <PlusIcon className="h-4 w-4" />
            Enroll in Course
          </Button>
        </div>
      </div>

      {/* Quick Stats - Glass iOS style */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-glass border-white/50">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <AcademicCapIcon className="h-8 w-8 text-blue-600" />
            </div>
            <p className="text-xl font-bold text-blue-600">
              {(student.enrolledCourses || []).filter(c => c.assignmentStatus !== 'transferred').length}
            </p>
            <p className="text-sm text-gray-600 font-data">Enrolled Courses</p>
          </CardContent>
        </Card>
        <Card className="card-glass border-white/50">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <CurrencyRupeeIcon className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-xl font-bold text-green-600">₹{(student.paymentSummary?.totalAmountPaid || 0).toLocaleString()}</p>
            <p className="text-sm text-gray-600 font-data">Total Paid</p>
          </CardContent>
        </Card>
        <Card className="card-glass border-white/50">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <ClockIcon className="h-8 w-8 text-orange-600" />
            </div>
            <p className="text-xl font-bold text-orange-600">₹{(student.paymentSummary?.pendingAmount || 0).toLocaleString()}</p>
            <p className="text-sm text-gray-600 font-data">Pending Amount</p>
          </CardContent>
        </Card>
        <Card className="card-glass border-white/50">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <CalendarIcon className="h-8 w-8 text-violet-600" />
            </div>
            <p className="text-xl font-bold text-violet-600">
              {new Date(student.createdAt).toLocaleDateString()}
            </p>
            <p className="text-sm text-gray-600 font-data">Joined Date</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Personal Details */}
        <div className="lg:col-span-1 space-y-6">
          {/* Basic Information */}
          <Card className="card-glass border-white/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="h-5 w-5 text-blue-600" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {student.studentId && (
                <div className="flex items-start gap-3">
                  <IdentificationIcon className="h-5 w-5 text-violet-500 mt-0.5" />
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Student ID</Label>
                    <p className="text-sm font-mono font-semibold text-violet-700">{student.studentId}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <EnvelopeIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <Label className="text-xs text-gray-500 font-data">Email</Label>
                  <p className="text-sm font-medium font-data">{student.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <PhoneIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <Label className="text-xs text-gray-500 font-data">Phone</Label>
                  <p className="text-sm font-medium font-data">{student.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <Label className="text-xs text-gray-500">Address</Label>
                  {student.address ? (
                    <p className="text-sm font-medium">
                      {student.address.street && `${student.address.street}, `}
                      {student.address.city && `${student.address.city}, `}
                      {student.address.state && `${student.address.state} `}
                      {student.address.pincode}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400">Not provided</p>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <DocumentIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <Label className="text-xs text-gray-500">Admission Source</Label>
                  <Badge variant="outline" className="mt-1">
                    {student.admissionSource ? student.admissionSource.charAt(0).toUpperCase() + student.admissionSource.slice(1) : 'N/A'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Guardian Information */}
          {/* {student.guardian && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UsersIcon className="h-5 w-5 text-violet-600" />
                  Guardian Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Name</Label>
                  <p className="text-sm font-medium">{student.guardian.name || 'Not provided'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Relationship</Label>
                  <p className="text-sm font-medium">{student.guardian.relationship || 'Not provided'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Phone</Label>
                  <p className="text-sm font-medium">{student.guardian.phone || 'Not provided'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Email</Label>
                  <p className="text-sm font-medium">{student.guardian.email || 'Not provided'}</p>
                </div>
              </CardContent>
            </Card>
          )} */}

          {/* Documents */}
          <Card className="card-glass border-white/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DocumentIcon className="h-5 w-5 text-green-600" />
                Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {student.documents && student.documents.length > 0 ? (
                student.documents.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{getDocumentDisplayName(doc.type)}</p>
                      <p className="text-xs text-gray-500">{doc.fileName}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleOpenDocument(doc.fileUrl)}
                      >
                        <EyeIcon className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <p>No documents uploaded</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Course & Payment Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Enrolled Courses */}
          <Card className="card-glass border-white/50">
            <CardHeader>
              <CardTitle>Enrolled Courses & Batches</CardTitle>
              <CardDescription className="font-data">Course enrollments with batch and trainer details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {student.enrolledCourses && student.enrolledCourses.length > 0 ? (
                student.enrolledCourses.map((course, index) => {
                  const isTransferred = course.assignmentStatus === 'transferred';
                  return (
                  <Card key={index} className={`border-2 ${isTransferred ? 'border-gray-200 opacity-80' : ''}`}>
                    <CardContent className="p-4">
                      {/* Course Header */}
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className={`font-semibold text-base ${isTransferred ? 'text-gray-500' : ''}`}>
                              {course.courseName}
                            </h4>
                            {isTransferred && (
                              <Badge className="bg-gray-100 text-gray-500 border border-gray-300 text-xs font-normal">
                                Past Enrollment
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            Enrolled: {new Date(course.enrollmentDate).toLocaleDateString()}
                          </p>
                        </div>
                        {isTransferred ? (
                          <div className="flex flex-col items-end gap-1">
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-xs">
                              Transferred
                            </Badge>
                            {course.transferredTo && (
                              <span className="text-xs text-gray-500">
                                → {course.transferredTo.courseName} / {course.transferredTo.batchName}
                              </span>
                            )}
                          </div>
                        ) : (
                          <Badge className={getPaymentStatusColor(course.paymentStatus)}>
                            {course.paymentStatus || 'pending'}
                          </Badge>
                        )}
                      </div>

                      {/* Batch Information */}
                      <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                        <Label className="text-sm font-semibold text-blue-800 mb-2 block">Batch Details</Label>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-xs text-blue-600">Batch Name</p>
                            <p className="font-medium">{course.batchName}</p>
                          </div>
                          <div>
                            <p className="text-xs text-blue-600">Mode</p>
                            <Badge variant="outline" className="capitalize">
                              {course.mode}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-xs text-blue-600">Duration</p>
                            <p className="font-medium">
                              {new Date(course.startDate).toLocaleDateString()} - {new Date(course.endDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-blue-600">Status</p>
                            <Badge className={getPaymentStatusColor(course.status)}>
                              {course.status}
                            </Badge>
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-blue-600">Schedule</p>
                            <p className="text-xs font-medium text-gray-700 mt-0.5">
                              {formatScheduleCompact(course.schedule) || '—'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Trainer Information */}
                      {course.trainers && course.trainers.length > 0 && (
                        <div className="mb-3 p-3 bg-violet-50 rounded-lg">
                          <Label className="text-sm font-semibold text-violet-800 mb-2 block">Trainer Details</Label>
                          <div className="space-y-2">
                            {course.trainers.map((trainer, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <div className="h-12 w-12 rounded-full bg-violet-200 flex items-center justify-center">
                                  {trainer.image ? (
                                    <img src={trainer.image} alt={trainer.name} className="h-12 w-12 rounded-full object-cover" />
                                  ) : (
                                    <UserIcon className="h-6 w-6 text-violet-600" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-semibold text-sm">{trainer.name}</p>
                                  <p className="text-xs text-gray-500">{trainer.email} • {trainer.phone}</p>
                                  <Badge variant="outline" className="text-xs mt-1">
                                    {trainer.role}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Payment Summary */}
                      {isTransferred ? (
                        <div className="p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500">Amount Paid on this Course</p>
                              <p className="font-semibold text-green-600">
                                ₹{(course.amountPaid || 0).toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Course Fee</p>
                              <p className="font-semibold text-gray-500">
                                ₹{(course.totalAmount || 0).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          {course.transferredTo && (
                            <p className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                              <span>↳</span>
                              <span>
                                Payments credited / transferred to{' '}
                                <strong>{course.transferredTo.courseName}</strong> –{' '}
                                {course.transferredTo.batchName}
                              </span>
                            </p>
                          )}
                        </div>
                      ) : (
                      <div className="grid grid-cols-3 gap-4 p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-600">Amount Paid</p>
                          <p className="font-semibold text-green-600">
                            ₹{(course.amountPaid || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Total Amount</p>
                          <p className="font-semibold text-blue-600">
                            ₹{(course.totalAmount || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Pending</p>
                          <p className="font-semibold text-orange-600">
                            ₹{(course.pendingAmount || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      )}

                      {/* Fee Installments — only for the current (non-transferred) invoice */}
                      {(() => {
                        if (isTransferred) return null;
                        const activeInvoice = student.feeInvoices?.find(
                          inv =>
                            String(inv.batchId) === String(course.batchId) &&
                            inv.status !== 'transferred'
                        );
                        if (!activeInvoice) return null;

                        // Check whether this invoice was created via a transfer
                        // (i.e. a credit was carried forward from the old course)
                        const creditApplied = activeInvoice.transferMetadata?.creditAmount ||
                          activeInvoice.installments.reduce((s, i) => s + (i.paidAmount || 0), 0);

                        return (
                          <div className="mt-3 p-3 bg-orange-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-sm font-semibold text-orange-800">Fee Installments</Label>
                              {creditApplied > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                                  <ArrowPathIcon className="h-3 w-3" />
                                  ₹{creditApplied.toLocaleString()} credited from previous course
                                </span>
                              )}
                            </div>
                            <div className="space-y-2">
                              {activeInvoice.installments.map((installment, idx) => (
                                <div key={idx} className="flex justify-between items-center p-2 bg-white rounded border">
                                  <div>
                                    <p className="text-sm font-medium">Installment {installment.installmentNo}</p>
                                    <p className="text-xs text-gray-500">
                                      Due: {new Date(installment.dueDate).toLocaleDateString()}
                                    </p>
                                    {installment.paidAmount > 0 && installment.status !== 'pending' && (
                                      <p className="text-xs text-green-600">
                                        Paid: ₹{installment.paidAmount.toLocaleString()}
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-semibold">₹{installment.amount.toLocaleString()}</p>
                                    {installment.remainingAmount > 0 && (
                                      <p className="text-xs text-orange-600">
                                        Due: ₹{installment.remainingAmount.toLocaleString()}
                                      </p>
                                    )}
                                    <Badge className={getPaymentStatusColor(installment.status) + " text-xs"}>
                                      {installment.status}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No courses enrolled yet</p>
                  <Button
                    onClick={handleEnrollInCourse}
                    className="mt-4"
                  >
                    Enroll in First Course
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transfer History */}
          {student.transferHistory && student.transferHistory.length > 0 && (
            <Card className="card-glass border-white/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowPathIcon className="h-5 w-5 text-amber-600" />
                  Transfer History
                </CardTitle>
                <CardDescription className="font-data">
                  Course transfer records with fee credit details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {student.transferHistory.map((transfer, idx) => (
                  <div key={idx} className="border border-amber-200 rounded-xl overflow-hidden">
                    {/* from → to header */}
                    <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-orange-50">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 mb-0.5">From</p>
                        <p className="font-semibold text-sm text-gray-800 truncate">
                          {transfer.fromCourse?.name || 'Unknown Course'}
                        </p>
                        {transfer.fromBatch && (
                          <p className="text-xs text-gray-500">{transfer.fromBatch.name}</p>
                        )}
                      </div>
                      <ArrowRightIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0 text-right">
                        <p className="text-xs text-gray-500 mb-0.5">To</p>
                        <p className="font-semibold text-sm text-green-700 truncate">
                          {transfer.toCourse?.name || 'Unknown Course'}
                        </p>
                        {transfer.toBatch && (
                          <p className="text-xs text-gray-500">{transfer.toBatch.name}</p>
                        )}
                      </div>
                    </div>

                    {/* Financial details */}
                    <div className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white">
                      <div className="p-2 bg-green-50 rounded-lg text-center">
                        <p className="text-xs text-green-600 mb-0.5">Amount Paid</p>
                        <p className="font-bold text-sm text-green-700">
                          ₹{(transfer.originalPaidAmount || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="p-2 bg-blue-50 rounded-lg text-center">
                        <p className="text-xs text-blue-600 mb-0.5">Credit Applied</p>
                        <p className="font-bold text-sm text-blue-700">
                          ₹{(transfer.creditAmount || 0).toLocaleString()}
                        </p>
                      </div>
                      {transfer.refundAmount > 0 && (
                        <div className="p-2 bg-violet-50 rounded-lg text-center">
                          <p className="text-xs text-violet-600 mb-0.5">Refund Amount</p>
                          <p className="font-bold text-sm text-violet-700">
                            ₹{transfer.refundAmount.toLocaleString()}
                          </p>
                        </div>
                      )}
                      {transfer.transferFee > 0 && (
                        <div className="p-2 bg-orange-50 rounded-lg text-center">
                          <p className="text-xs text-orange-600 mb-0.5">Transfer Fee</p>
                          <p className="font-bold text-sm text-orange-700">
                            ₹{transfer.transferFee.toLocaleString()}
                          </p>
                        </div>
                      )}
                      <div className="p-2 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-500 mb-0.5">New Course Total</p>
                        <p className="font-bold text-sm text-gray-700">
                          ₹{(transfer.newInvoiceTotal || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {/* Date, reason, adjustment type */}
                    <div className="px-3 pb-3 pt-1 bg-white space-y-1">
                      <div className="flex flex-wrap gap-2 items-center text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="h-3.5 w-3.5" />
                          {new Date(transfer.transferDate).toLocaleDateString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })}
                        </span>
                        {transfer.feeAdjustmentType && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {transfer.feeAdjustmentType.replace(/_/g, ' ')}
                          </Badge>
                        )}
                        <Badge className={
                          transfer.newInvoiceStatus === 'paid'
                            ? 'bg-green-100 text-green-800 text-xs'
                            : transfer.newInvoiceStatus === 'partial'
                            ? 'bg-orange-100 text-orange-800 text-xs'
                            : 'bg-gray-100 text-gray-700 text-xs'
                        }>
                          {transfer.newInvoiceStatus}
                        </Badge>
                      </div>
                      {transfer.reason && (
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Reason:</span> {transfer.reason}
                        </p>
                      )}
                      {transfer.adjustmentNotes && transfer.adjustmentNotes.length > 0 && (
                        <div className="mt-1 p-2 bg-amber-50 rounded text-xs text-amber-800">
                          {transfer.adjustmentNotes.map((note, i) => (
                            <p key={i}>{note}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Student Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-2xl max-h-[92vh] flex flex-col">
          {/* Gradient header */}
          <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-5 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center">
                <PencilIcon className="h-4 w-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-white">Edit Student Details</h2>
                <p className="text-xs text-violet-200 mt-0.5">
                  {editedStudent?.name ? `Updating ${editedStudent.name}` : 'Update student information'}
                </p>
              </div>
            </div>
          </div>
          {editedStudent && (
            <form onSubmit={handleSaveEdit} className="flex flex-col flex-1 overflow-hidden">
              <div className="grid gap-4 py-4 px-6 overflow-y-auto flex-1">
                {/* Basic Information */}
                <div className="space-y-4">
                  <p className="text-[11px] font-semibold text-violet-600 uppercase tracking-widest">Basic Information</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-name">Full Name *</Label>
                      <Input
                        id="edit-name"
                        value={editedStudent.name}
                        onChange={(e) => setEditedStudent({ ...editedStudent, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-email">Email *</Label>
                      <Input
                        id="edit-email"
                        type="email"
                        value={editedStudent.email}
                        onChange={(e) => setEditedStudent({ ...editedStudent, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-phone">Phone Number *</Label>
                      <Input
                        id="edit-phone"
                        value={editedStudent.phone}
                        onChange={(e) => setEditedStudent({ ...editedStudent, phone: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-admissionSource">Admission Source</Label>
                      <Select
                        value={editedStudent.admissionSource}
                        onValueChange={(value) => setEditedStudent({ ...editedStudent, admissionSource: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="website">Website</SelectItem>
                          <SelectItem value="walkin">Walk-in</SelectItem>
                          <SelectItem value="reference">Reference</SelectItem>
                          <SelectItem value="phone_enquiry">Phone Enquiry</SelectItem>
                          <SelectItem value="social">Social Media</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Guardian Information */}
                {/* <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Guardian Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-guardianName">Guardian Name</Label>
                      <Input
                        id="edit-guardianName"
                        value={editedStudent.guardian.name}
                        onChange={(e) => setEditedStudent({
                          ...editedStudent,
                          guardian: { ...editedStudent.guardian, name: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-guardianPhone">Guardian Phone</Label>
                      <Input
                        id="edit-guardianPhone"
                        value={editedStudent.guardian.phone}
                        onChange={(e) => setEditedStudent({
                          ...editedStudent,
                          guardian: { ...editedStudent.guardian, phone: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-guardianEmail">Guardian Email</Label>
                      <Input
                        id="edit-guardianEmail"
                        type="email"
                        value={editedStudent.guardian.email}
                        onChange={(e) => setEditedStudent({
                          ...editedStudent,
                          guardian: { ...editedStudent.guardian, email: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-relationship">Relationship</Label>
                      <Select
                        value={editedStudent.guardian.relationship}
                        onValueChange={(value) => setEditedStudent({
                          ...editedStudent,
                          guardian: { ...editedStudent.guardian, relationship: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="father">Father</SelectItem>
                          <SelectItem value="mother">Mother</SelectItem>
                          <SelectItem value="guardian">Guardian</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div> */}

                {/* Address Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Address</h3>
                  <div className="space-y-2">
                    <Label htmlFor="edit-street">Street Address</Label>
                    <Input
                      id="edit-street"
                      value={editedStudent.address.street}
                      onChange={(e) => setEditedStudent({
                        ...editedStudent,
                        address: { ...editedStudent.address, street: e.target.value }
                      })}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-city">City</Label>
                      <Input
                        id="edit-city"
                        value={editedStudent.address.city}
                        onChange={(e) => setEditedStudent({
                          ...editedStudent,
                          address: { ...editedStudent.address, city: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-state">State</Label>
                      <Input
                        id="edit-state"
                        value={editedStudent.address.state}
                        onChange={(e) => setEditedStudent({
                          ...editedStudent,
                          address: { ...editedStudent.address, state: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-pincode">Pincode</Label>
                      <Input
                        id="edit-pincode"
                        value={editedStudent.address.pincode}
                        onChange={(e) => setEditedStudent({
                          ...editedStudent,
                          address: { ...editedStudent.address, pincode: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Documents Section */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Documents</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {['photo', 'adhaar', 'pancard', 'id'].map((docType) => {
                      const existingDoc = editedStudent.documents?.find(doc => doc.type === docType);
                      return (
                        <div key={docType} className="space-y-2">
                          <Label htmlFor={`file-${docType}`}>
                            {getDocumentDisplayName(docType)}
                            {existingDoc && <CheckCircleIcon className="h-4 w-4 text-green-500 inline ml-1" />}
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              id={`file-${docType}`}
                              type="file"
                              accept={docType === 'photo' ? 'image/*' : '*/*'}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleFileUpload(file, docType);
                                }
                              }}
                              disabled={uploadingFile && currentUploadType === docType}
                              className="flex-1"
                            />
                            {existingDoc && (
                              <div className="flex gap-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenDocument(existingDoc.fileUrl)}
                                  disabled={uploadingFile}
                                >
                                  <EyeIcon className="h-3 w-3" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleRemoveDocument(docType, existingDoc.fileUrl)}
                                  disabled={uploadingFile || removingDocType === docType}
                                >
                                  {removingDocType === docType ? (
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                  ) : (
                                    <TrashIcon className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                          {uploadingFile && currentUploadType === docType && (
                            <p className="text-xs text-blue-600">Uploading...</p>
                          )}
                          {existingDoc && (
                            <p className="text-xs text-gray-500 truncate">
                              {existingDoc.fileName}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
                <Button type="button" variant="outline" size="sm" onClick={() => setIsEditDialogOpen(false)} className="text-sm">Cancel</Button>
                <Button type="submit" size="sm" disabled={loading}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-sm px-6 gap-2">
                  {loading
                    ? <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Saving…</>
                    : <><PencilIcon className="h-3.5 w-3.5" /> Save Changes</>}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Enroll in Course Dialog ─────────────────────────────────────────── */}
      <Dialog open={isEnrollDialogOpen} onOpenChange={v => { setIsEnrollDialogOpen(v); if (!v) { setEnrollmentData(blankEnrollment); setAvailableBatches([]); } }}>
        <DialogContent className="max-w-[95vw] sm:max-w-[640px] max-h-[92vh] p-0 gap-0 flex flex-col overflow-y-auto">

          {/* Gradient header */}
          <div className="bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <AcademicCapIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-white leading-tight">Enroll in a Course</h2>
                <p className="text-xs text-white/70 mt-0.5 truncate">{student.name}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleEnrollmentSubmit} className="flex flex-col">
            <div className="px-6 py-4 space-y-4">

              {/* Course Selection */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Select Course <span className="text-red-500">*</span></Label>
                <Select value={enrollmentData.courseId} onValueChange={handleCourseChange}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Choose a course" /></SelectTrigger>
                  <SelectContent>
                    {courses.map((course) => {
                      const alreadyEnrolled = student.enrolledCourses?.some(
                        c => String(c.courseId) === String(course._id)
                      );
                      return (
                        <SelectItem key={course._id} value={course._id}
                          disabled={alreadyEnrolled}
                          className={alreadyEnrolled ? 'opacity-60 cursor-not-allowed' : ''}>
                          {course.courseName}
                          {' · '}Online: ₹{(course.onlineFee ?? 0).toLocaleString()}
                          {' / '}Offline: ₹{(course.offlineFee ?? 0).toLocaleString()}
                          {alreadyEnrolled && ' — Already Enrolled'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Batch Cards */}
              {enrollmentData.courseId && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-gray-700">Select Batch <span className="text-red-500">*</span></Label>
                  {availableBatches.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {availableBatches.map((batch) => {
                        const isFull = isBatchFull(batch);
                        const seats  = batch.capacity - batch.assignedStudents;
                        const sel    = enrollmentData.batchId === batch._id;
                        const timing = formatScheduleCompact(batch.schedule);
                        return (
                          <div key={batch._id}
                            onClick={() => !isFull && setEnrollmentData(p => ({ ...p, batchId: batch._id }))}
                            className={`p-3.5 border-2 rounded-xl cursor-pointer transition-all ${
                              sel  ? 'border-blue-500 bg-blue-50/60 shadow-sm' :
                              isFull ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' :
                                       'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40'
                            }`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="font-semibold text-sm text-gray-900">{batch.name}</span>
                                  <Badge className={`text-[10px] px-1.5 py-0 capitalize ${batch.mode === 'online' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{batch.mode}</Badge>
                                  {isFull
                                    ? <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700">Full</Badge>
                                    : <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700">{seats} seat{seats !== 1 ? 's' : ''}</Badge>}
                                </div>
                                <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">
                                  {timing && <span className="flex items-center gap-1"><ClockIcon className="h-3 w-3 text-blue-400"/>{timing}</span>}
                                  <span className="flex items-center gap-1"><UserIcon className="h-3 w-3"/>{batch.assignedStudents}/{batch.capacity}</span>
                                  <span>{new Date(batch.startDate).toLocaleDateString()} – {new Date(batch.endDate).toLocaleDateString()}</span>
                                </div>
                                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                                  <div className={`h-1.5 rounded-full transition-all ${isFull ? 'bg-red-400' : sel ? 'bg-blue-500' : 'bg-gray-400'}`}
                                    style={{ width: `${Math.min(100,(batch.assignedStudents/batch.capacity)*100)}%` }} />
                                </div>
                              </div>
                              {sel && <CheckCircleSolid className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-6 text-center border-2 border-dashed border-gray-200 rounded-xl">
                      <ExclamationTriangleIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No active batches for this course</p>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Setup */}
              {enrollmentData.batchId && selectedCourse && selectedBatch && (() => {
                const finalFee = enrollmentData.customTotalAmount ? Number(enrollmentData.customTotalAmount) : applicableFee;
                const n        = Number(enrollmentData.numInstallments);
                const amounts  = enrollmentData.installmentAmounts;
                const amtSum   = amounts.reduce((s, a) => s + (Number(a) || 0), 0);
                const amtOk    = amounts.length === n && Math.abs(amtSum - finalFee) <= 1;
                const discount = applicableFee - finalFee;
                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 py-1">
                      <div className="h-px flex-1 bg-gray-100" />
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-600 px-2">Payment Setup</span>
                      <div className="h-px flex-1 bg-gray-100" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Final Fee <span className="text-gray-400">(optional)</span></Label>
                        <Input type="number" min="0" className="h-9 text-sm"
                          value={enrollmentData.customTotalAmount}
                          onChange={e => setEnrollmentData(p => ({ ...p, customTotalAmount: e.target.value, installmentAmounts: [] }))}
                          placeholder={`Default: ₹${applicableFee.toLocaleString()}`} />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Adjustment Reason <span className="text-gray-400">(optional)</span></Label>
                        <Input className="h-9 text-sm"
                          value={enrollmentData.discountReason}
                          onChange={e => setEnrollmentData(p => ({ ...p, discountReason: e.target.value }))}
                          placeholder="e.g., scholarship" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Payment Plan</Label>
                        <Select value={enrollmentData.paymentPlan}
                          onValueChange={v => setEnrollmentData(p => ({ ...p, paymentPlan: v, installmentAmounts: [] }))}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="full">💳 Full Payment</SelectItem>
                            <SelectItem value="emi">📅 EMI / Installments</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {enrollmentData.paymentPlan === 'emi' && (
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Number of Installments</Label>
                          <Select value={String(n)}
                            onValueChange={v => {
                              const newN = Number(v); const eq = Math.floor(finalFee/newN); const last = Math.round(finalFee - eq*(newN-1));
                              setEnrollmentData(p => ({ ...p, numInstallments: newN, installmentAmounts: Array.from({length:newN},(_,i)=>i===newN-1?last:eq) }));
                            }}>
                            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[2,3,4].map(i => <SelectItem key={i} value={String(i)}>{i} installments</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    {enrollmentData.paymentPlan === 'emi' && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-gray-600">Installment amounts</p>
                          <button type="button" className="text-[11px] text-blue-600 hover:underline"
                            onClick={() => { const eq=Math.floor(finalFee/n); const last=Math.round(finalFee-eq*(n-1)); setEnrollmentData(p=>({...p,installmentAmounts:Array.from({length:n},(_,i)=>i===n-1?last:eq)})); }}>
                            Reset to equal
                          </button>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {Array.from({length:n}).map((_,i)=>(
                            <div key={i} className="space-y-1">
                              <p className="text-[10px] font-medium text-gray-500 uppercase">Inst. {i+1}</p>
                              <Input type="number" min="1" className="h-8 text-xs"
                                value={amounts[i]??''}
                                onChange={e=>{const u=[...amounts];u[i]=e.target.value;setEnrollmentData(p=>({...p,installmentAmounts:u}));}}
                                placeholder={`₹${Math.floor(finalFee/n).toLocaleString()}`} />
                            </div>
                          ))}
                        </div>
                        <div className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg ${amtOk?'bg-emerald-50 text-emerald-700':'bg-amber-50 text-amber-700'}`}>
                          <span>Total: <strong>₹{amtSum.toLocaleString()}</strong></span>
                          <span>Required: <strong>₹{finalFee.toLocaleString()}</strong></span>
                          {amtOk?<span className="font-semibold">✓ Matches</span>:<span>Diff: ₹{Math.abs(finalFee-amtSum).toLocaleString()}</span>}
                        </div>
                      </div>
                    )}
                    {/* Summary */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-1.5 text-sm">
                      <p className="text-[10px] font-bold text-blue-800 uppercase tracking-widest mb-2">Enrollment Summary</p>
                      {[
                        { label:'Student', value:student.name },
                        { label:'Course',  value:selectedCourse.courseName },
                        { label:'Batch',   value:selectedBatch.name },
                        { label:`Fee (${selectedBatch.mode})`, value:`₹${applicableFee.toLocaleString()}`, bold:true },
                        ...(discount>0?[{label:'Discount',value:`-₹${discount.toLocaleString()}`,cls:'text-emerald-700'}]:[]),
                        ...(discount>0?[{label:'Final Fee',value:`₹${finalFee.toLocaleString()}`,bold:true,cls:'text-blue-700'}]:[]),
                        { label:'Plan', value:enrollmentData.paymentPlan==='emi'?`EMI · ${n} installments`:'Full Payment' },
                        ...(enrollmentData.paymentPlan==='emi'&&amtOk
                          ? amounts.map((a,i)=>({label:`  Installment ${i+1}`,value:`₹${Number(a).toLocaleString()}`}))
                          : enrollmentData.paymentPlan==='emi'
                          ? [{label:'Per Installment',value:`≈ ₹${Math.floor(finalFee/n).toLocaleString()}`}]
                          : [])
                      ].map(({label,value,bold,cls})=>(
                        <div key={label} className="flex justify-between">
                          <span className="text-gray-600">{label}:</span>
                          <span className={`${bold?'font-semibold':'font-medium'} ${cls||''}`}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Sticky footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/80 sticky bottom-0">
              <Button type="button" variant="outline" size="sm" className="h-9 text-sm"
                onClick={() => { setIsEnrollDialogOpen(false); setEnrollmentData(blankEnrollment); setAvailableBatches([]); }}>
                Cancel
              </Button>
              <Button type="submit" size="sm"
                disabled={!enrollmentData.courseId || !enrollmentData.batchId || loading}
                className="h-9 px-6 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white gap-2">
                {loading
                  ? <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Enrolling…</>
                  : <><AcademicCapIcon className="h-3.5 w-3.5"/> Enroll Student</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete History Modal */}
      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex-col overflow-y-auto overflow-x-hidden border-white/50 shadow-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <DocumentTextIcon className="h-5 w-5 text-violet-600" />
              Complete Student History
            </DialogTitle>
            <DialogDescription className="font-data">
              View enrollments, payments, and attendance. Export to CSV for records.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button
              variant="glass"
              size="sm"
              onClick={handleExportCompleteHistory}
              disabled={exportingHistory}
              className="mb-2"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              {exportingHistory ? 'Exporting...' : 'Export to CSV'}
            </Button>

            {historyLoading ? (
              <div className="py-8 text-center text-gray-500">Loading history...</div>
            ) : (
              <Tabs defaultValue="enrollments" className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="enrollments">Enrollments</TabsTrigger>
                  <TabsTrigger value="payments">Payments</TabsTrigger>
                  <TabsTrigger value="attendance">Attendance</TabsTrigger>
                  <TabsTrigger value="transfers">
                    Transfers
                    {student.transferHistory?.length > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center h-4 w-4 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                        {student.transferHistory.length}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="refunds">
                    Refunds
                    {(historyData.refunds?.length > 0) && (
                      <span className="ml-1 inline-flex items-center justify-center h-4 w-4 rounded-full bg-red-500 text-white text-[10px] font-bold">
                        {historyData.refunds.length}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="enrollments" className="mt-4">
                  {/* Transfer history note */}
                  {student.transferHistory?.length > 0 && (
                    <div className="mb-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                      <ArrowPathIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
                      <span>
                        This student has {student.transferHistory.length} course transfer{student.transferHistory.length > 1 ? 's' : ''}.
                        Previously paid fees have been credited to the current course. See the <strong>Transfers</strong> tab for full details.
                      </span>
                    </div>
                  )}
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#6F00FF] hover:bg-[#6F00FF]">
                          <TableHead className="text-white font-semibold font-data">Course</TableHead>
                          <TableHead className="text-white font-semibold font-data">Batch</TableHead>
                          <TableHead className="text-white font-semibold font-data">Total Fee</TableHead>
                          <TableHead className="text-white font-semibold font-data">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(historyData.feeData?.courses || [])
                          .filter(c => c.status !== 'transferred')
                          .map((c, i) => (
                            <TableRow key={i}>
                              <TableCell>{c.course}</TableCell>
                              <TableCell>{c.batch}</TableCell>
                              <TableCell>₹{(c.totalFee || 0).toLocaleString()}</TableCell>
                              <TableCell><Badge className={getPaymentStatusColor(c.status)}>{c.status}</Badge></TableCell>
                            </TableRow>
                          ))}
                        {(!(historyData.feeData?.courses || []).filter(c => c.status !== 'transferred').length) && (
                          <TableRow><TableCell colSpan={4} className="text-center py-6 text-gray-500">No active enrollments</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
                <TabsContent value="payments" className="mt-4">
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#6F00FF] hover:bg-[#6F00FF]">
                          <TableHead className="text-white font-semibold font-data">Date</TableHead>
                          <TableHead className="text-white font-semibold font-data">Receipt No</TableHead>
                          <TableHead className="text-white font-semibold font-data">Amount</TableHead>
                          <TableHead className="text-white font-semibold font-data">Mode</TableHead>
                          <TableHead className="text-white font-semibold font-data">Installment</TableHead>
                          <TableHead className="text-white font-semibold font-data">Recorded By</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(historyData.feeData?.payments || []).map((p, i) => (
                          <TableRow key={i} className="hover:bg-violet-50/50">
                            <TableCell className="font-data">{p.date ? new Date(p.date).toLocaleDateString() : '-'}</TableCell>
                            <TableCell className="font-data">{p.receiptNo || '-'}</TableCell>
                            <TableCell className="text-green-600 font-medium">₹{(p.amount || 0).toLocaleString()}</TableCell>
                            <TableCell className="capitalize">{p.mode}</TableCell>
                            <TableCell>{p.installmentNo}</TableCell>
                            <TableCell>{p.recordedBy || '-'}</TableCell>
                          </TableRow>
                        ))}
                        {(!historyData.feeData?.payments?.length) && (
                          <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-500">No payments</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
                <TabsContent value="attendance" className="mt-4">
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm max-h-[320px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#6F00FF] hover:bg-[#6F00FF]">
                          <TableHead className="text-white font-semibold font-data">Date</TableHead>
                          <TableHead className="text-white font-semibold font-data">Batch</TableHead>
                          <TableHead className="text-white font-semibold font-data">Status</TableHead>
                          <TableHead className="text-white font-semibold font-data">Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(historyData.attendances || []).map((a) => (
                          <TableRow key={a._id} className="hover:bg-violet-50/50">
                            <TableCell className="font-data">{a.date ? new Date(a.date).toLocaleDateString() : '-'}</TableCell>
                            <TableCell className="font-data">{a.batchId?.name || '-'}</TableCell>
                            <TableCell>
                              <Badge className={
                                a.status === 'present' ? 'bg-green-100 text-green-800' :
                                a.status === 'absent' ? 'bg-red-100 text-red-800' :
                                a.status === 'excused' ? 'bg-amber-100 text-amber-800' :
                                a.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }>{a.status}</Badge>
                            </TableCell>
                            <TableCell className="text-xs max-w-[180px] truncate">{a.reason || a.notes || '-'}</TableCell>
                          </TableRow>
                        ))}
                        {(!historyData.attendances?.length) && (
                          <TableRow><TableCell colSpan={4} className="text-center py-6 text-gray-500">No attendance records</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
                <TabsContent value="transfers" className="mt-4">
                  {student.transferHistory && student.transferHistory.length > 0 ? (
                    <div className="space-y-3">
                      {student.transferHistory.map((transfer, idx) => (
                        <div key={idx} className="border border-amber-200 rounded-xl overflow-hidden">
                          {/* from → to */}
                          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-orange-50">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500">From Course</p>
                              <p className="font-semibold text-sm text-gray-800 truncate">
                                {transfer.fromCourse?.name || '—'}
                              </p>
                              {transfer.fromBatch && (
                                <p className="text-xs text-gray-500">{transfer.fromBatch.name}</p>
                              )}
                            </div>
                            <ArrowRightIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0 text-right">
                              <p className="text-xs text-gray-500">To Course</p>
                              <p className="font-semibold text-sm text-green-700 truncate">
                                {transfer.toCourse?.name || '—'}
                              </p>
                              {transfer.toBatch && (
                                <p className="text-xs text-gray-500">{transfer.toBatch.name}</p>
                              )}
                            </div>
                          </div>
                          {/* Financial summary table */}
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-amber-100 hover:bg-amber-100">
                                  <TableHead className="text-amber-900 font-semibold text-xs">Amount Paid</TableHead>
                                  <TableHead className="text-amber-900 font-semibold text-xs">Credit Applied</TableHead>
                                  <TableHead className="text-amber-900 font-semibold text-xs">Refund</TableHead>
                                  <TableHead className="text-amber-900 font-semibold text-xs">Transfer Fee</TableHead>
                                  <TableHead className="text-amber-900 font-semibold text-xs">New Total</TableHead>
                                  <TableHead className="text-amber-900 font-semibold text-xs">Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                <TableRow className="hover:bg-amber-50/50">
                                  <TableCell className="text-green-700 font-semibold">
                                    ₹{(transfer.originalPaidAmount || 0).toLocaleString()}
                                  </TableCell>
                                  <TableCell className="text-blue-700 font-semibold">
                                    ₹{(transfer.creditAmount || 0).toLocaleString()}
                                  </TableCell>
                                  <TableCell className="text-violet-700 font-semibold">
                                    {transfer.refundAmount > 0 ? `₹${transfer.refundAmount.toLocaleString()}` : '—'}
                                  </TableCell>
                                  <TableCell className="text-orange-700 font-semibold">
                                    {transfer.transferFee > 0 ? `₹${transfer.transferFee.toLocaleString()}` : '—'}
                                  </TableCell>
                                  <TableCell className="text-gray-800 font-semibold">
                                    ₹{(transfer.newInvoiceTotal || 0).toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    <Badge className={
                                      transfer.newInvoiceStatus === 'paid'
                                        ? 'bg-green-100 text-green-800 text-xs'
                                        : transfer.newInvoiceStatus === 'partial'
                                        ? 'bg-orange-100 text-orange-800 text-xs'
                                        : 'bg-gray-100 text-gray-700 text-xs'
                                    }>
                                      {transfer.newInvoiceStatus}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>
                          {/* Meta */}
                          <div className="px-3 pb-3 pt-1 bg-white">
                            <div className="flex flex-wrap gap-2 items-center text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="h-3.5 w-3.5" />
                                {new Date(transfer.transferDate).toLocaleDateString('en-IN', {
                                  day: '2-digit', month: 'short', year: 'numeric'
                                })}
                              </span>
                              {transfer.feeAdjustmentType && (
                                <Badge variant="outline" className="text-xs capitalize">
                                  {transfer.feeAdjustmentType.replace(/_/g, ' ')}
                                </Badge>
                              )}
                            </div>
                            {transfer.reason && (
                              <p className="text-xs text-gray-600 mt-1">
                                <span className="font-medium">Reason:</span> {transfer.reason}
                              </p>
                            )}
                            {transfer.adjustmentNotes?.length > 0 && (
                              <div className="mt-1 p-2 bg-amber-50 rounded text-xs text-amber-800">
                                {transfer.adjustmentNotes.map((note, i) => <p key={i}>{note}</p>)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-10 text-center text-gray-500">
                      <ArrowPathIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p>No transfer records</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="refunds" className="mt-4">
                  {(historyData.refunds?.length > 0) ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-gray-500 font-medium">
                          {historyData.refunds.length} refund record{historyData.refunds.length > 1 ? 's' : ''}
                        </p>
                        <p className="text-sm font-bold text-red-600">
                          Total Refunded: ₹{(historyData.refunds.reduce((s, r) => s + (r.amount || 0), 0)).toLocaleString()}
                        </p>
                      </div>
                      {historyData.refunds.map((refund, idx) => (
                        <div key={refund._id || idx} className="border border-red-200 rounded-xl overflow-hidden">
                          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-red-50 to-orange-50">
                            <div>
                              <p className="font-semibold text-sm text-gray-800">{refund.courseId?.name || '—'}</p>
                              <p className="text-xs text-gray-500">Batch: {refund.batchId?.name || '—'}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-red-600 text-base">₹{(refund.amount || 0).toLocaleString()}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                refund.status === 'processed' ? 'bg-green-100 text-green-700' :
                                refund.status === 'failed'    ? 'bg-red-100 text-red-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {refund.status}
                              </span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 text-xs bg-white">
                            <div>
                              <p className="text-gray-400 uppercase tracking-wide">Type</p>
                              <p className="font-medium capitalize mt-0.5">{(refund.refundType || '').replace('_', ' ')}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 uppercase tracking-wide">Mode</p>
                              <p className="font-medium capitalize mt-0.5">{refund.refundMode || '—'}</p>
                            </div>
                            {refund.referenceNo && (
                              <div>
                                <p className="text-gray-400 uppercase tracking-wide">Ref No.</p>
                                <p className="font-mono mt-0.5">{refund.referenceNo}</p>
                              </div>
                            )}
                            <div>
                              <p className="text-gray-400 uppercase tracking-wide">Date</p>
                              <p className="mt-0.5">{refund.processedAt ? new Date(refund.processedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 uppercase tracking-wide">Processed By</p>
                              <p className="mt-0.5">{refund.processedBy?.name || '—'}</p>
                            </div>
                          </div>
                          {refund.reason && (
                            <div className="px-3 pb-3 text-xs text-gray-600 bg-white border-t border-red-100">
                              <span className="font-medium">Reason: </span>{refund.reason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-10 text-center text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                      </svg>
                      <p>No refund records found</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
